import { useEffect, useRef, useState } from "react";
import { useInView, useReducedMotion } from "motion/react";
import { useLocation } from "react-router-dom";

import { REVEAL_VIEWPORT } from "@/app/common/functions/motion/config";

/** A ref for the element to reveal, and whether it has scrolled into view. */
export function useReveal<T extends HTMLElement = HTMLDivElement>() {
  const ref = useRef<T>(null);
  const inView = useInView(ref, REVEAL_VIEWPORT);
  const shouldReduceMotion = useReducedMotion();
  const { pathname } = useLocation();

  const [hasRevealed, setHasRevealed] = useState(false);
  const [prevPathname, setPrevPathname] = useState(pathname);

  // Reset state when route changes (React's recommended pattern for adjusting state during render)
  if (pathname !== prevPathname) {
    setPrevPathname(pathname);
    setHasRevealed(false);
  }

  // Trigger reveal during render when element becomes in view
  if (inView && !hasRevealed) {
    setHasRevealed(true);
  }

  // Handle reduced motion preference post-hydration asynchronously
  useEffect(() => {
    if (!shouldReduceMotion || hasRevealed) return;

    const rAFId = requestAnimationFrame(() => {
      setHasRevealed(true);
    });
    return () => cancelAnimationFrame(rAFId);
  }, [shouldReduceMotion, hasRevealed]);

  // Fallback for anything already intersecting the viewport at mount —
  // covers both scrolled-past-above (rect.bottom < 0) and, critically,
  // already-visible-from-the-start content like a page hero (rect.top <
  // innerHeight). The latter case relies entirely on useInView's own
  // IntersectionObserver callback above; if that callback is ever slow, or
  // doesn't fire cleanly for an element that's already intersecting the
  // instant observation begins (2026-08-19 — observed: hero/heading text
  // stuck invisible while below-the-fold content that gets a genuine
  // scroll-crossing event revealed fine), this synchronous rect check
  // reveals it regardless, independent of useInView's timing entirely. Done
  // inside an async requestAnimationFrame loop, after ScrollRestoration and
  // layout shifts settle, to avoid synchronous cascading renders.
  useEffect(() => {
    const el = ref.current;
    if (!el || hasRevealed || shouldReduceMotion) return;

    const rAFId = requestAnimationFrame(() => {
      const rect = el.getBoundingClientRect();
      if (rect.bottom < 0 || rect.top < window.innerHeight) {
        setHasRevealed(true);
      }
    });

    return () => cancelAnimationFrame(rAFId);
  }, [pathname, hasRevealed, shouldReduceMotion]);

  return [ref, hasRevealed] as const;
}
